/**
 * Visao em tabela da biblioteca: a forma certa para centenas de modelos.
 *
 * Decisoes nao obvias:
 *  1. Cabecalho ordenavel com `aria-sort` e um botao por coluna: clicar na
 *     coluna ja ordenada inverte o sentido; clicar noutra usa o sentido
 *     natural dela (numero do maior, nome de A a Z).
 *  2. So existe em tela media para cima (`md:`). Abaixo disso a pagina mostra
 *     os cartoes: dez colunas espremidas em 360px nao sao lidas por ninguem.
 *     Em telas medias a tabela rola na horizontal dentro do proprio quadro,
 *     com o cabecalho sempre junto.
 *  3. A tabela so recebe a FATIA visivel (o "ver mais" mora na pagina): nunca
 *     renderiza a biblioteca inteira.
 */

import { Badge, CompanyLogo, Icon } from '../../../components/ui';
import { formatBRLFromCents, formatCount, formatDateTime, formatRatio } from '../../../lib/format';
import type { CompanySummary } from '../../../types';
import type { Ranking } from '../desempenho';
import type { Ordem } from '../filtragem';
import type { MessageTemplate } from '../types';
import { CANAL_ICONE, STATUS_TOM, descreverPublico, statusLabel, type ColunaOrdem } from '../vocabulario';
import { Variacao } from './Variacao';

function Cabecalho({
  coluna,
  rotulo,
  ordem,
  onOrdenar,
  alinhar = 'right',
  dica,
}: {
  readonly coluna: ColunaOrdem;
  readonly rotulo: string;
  readonly ordem: Ordem;
  readonly onOrdenar: (coluna: ColunaOrdem) => void;
  readonly alinhar?: 'left' | 'right';
  readonly dica: string;
}): JSX.Element {
  const ativa = ordem.coluna === coluna;
  return (
    <th scope="col" aria-sort={ativa ? (ordem.desc ? 'descending' : 'ascending') : 'none'} className={`px-3 py-2 font-medium ${alinhar === 'right' ? 'text-right' : 'text-left'}`}>
      <button
        type="button"
        onClick={() => onOrdenar(coluna)}
        title={dica}
        className={`inline-flex items-center gap-1 rounded px-1 py-0.5 transition-colors hover:text-ink ${ativa ? 'text-ink' : ''}`}
      >
        {rotulo}
        <span aria-hidden="true" className={`text-[10px] ${ativa ? 'text-brand' : 'text-ink-subtle/60'}`}>
          {ativa ? (ordem.desc ? '▼' : '▲') : '↕'}
        </span>
      </button>
    </th>
  );
}

export function TabelaModelos({
  itens,
  empresaDe,
  ranking,
  ordem,
  onOrdenar,
  selecionados,
  podeSelecionarMais,
  onAbrir,
  onComparar,
}: {
  readonly itens: readonly MessageTemplate[];
  readonly empresaDe: (t: MessageTemplate) => CompanySummary | null;
  readonly ranking: Ranking;
  readonly ordem: Ordem;
  readonly onOrdenar: (coluna: ColunaOrdem) => void;
  readonly selecionados: readonly string[];
  readonly podeSelecionarMais: boolean;
  readonly onAbrir: (t: MessageTemplate) => void;
  readonly onComparar: (t: MessageTemplate) => void;
}): JSX.Element {
  return (
    <div className="overflow-x-auto rounded-2xl border border-line bg-surface shadow-soft">
      <table className="w-full min-w-[68rem] text-sm">
        <caption className="sr-only">Modelos da biblioteca com os números do período. Clique no cabeçalho para ordenar.</caption>
        <thead className="border-b border-line bg-elevated text-xs text-ink-subtle">
          <tr>
            <th scope="col" className="w-10 px-3 py-2">
              <span className="sr-only">Comparar</span>
            </th>
            <Cabecalho coluna="nome" rotulo="Modelo" ordem={ordem} onOrdenar={onOrdenar} alinhar="left" dica="Ordenar pelo nome do modelo" />
            <th scope="col" className="px-3 py-2 text-left font-medium">
              Fala com quem
            </th>
            <Cabecalho coluna="enviados" rotulo="Receberam" ordem={ordem} onOrdenar={onOrdenar} dica="Envios (ou ligações) no período" />
            <Cabecalho coluna="respostas" rotulo="Responderam" ordem={ordem} onOrdenar={onOrdenar} dica="Responderam e caíram com a vendedora; a régua parou para eles" />
            <th scope="col" className="px-3 py-2 text-right font-medium" title="Clicaram no link, medido pelo redirecionador da régua">
              Clicaram
            </th>
            <Cabecalho coluna="pagaram" rotulo="Pagaram" ordem={ordem} onOrdenar={onOrdenar} dica="Pagaram em até 7 dias com o crédito neste toque (último contato)" />
            <Cabecalho coluna="taxa_pagamento" rotulo="Taxa" ordem={ordem} onOrdenar={onOrdenar} dica="Pagaram ÷ receberam" />
            <Cabecalho coluna="recuperado" rotulo="Recuperado" ordem={ordem} onOrdenar={onOrdenar} dica="Valor pago por quem voltou com o crédito neste toque" />
            <Cabecalho coluna="atualizado" rotulo="Atualizado" ordem={ordem} onOrdenar={onOrdenar} dica="Última alteração do modelo" />
          </tr>
        </thead>
        <tbody className="divide-y divide-line/70">
          {itens.map((t) => {
            const p = t.performance;
            const a = t.previousPerformance;
            const empresa = empresaDe(t);
            const pos = ranking.get(t.id) ?? null;
            const selecionado = selecionados.includes(t.id);
            const credita = t.audience === 'checkout_abandonado' || t.audience === 'plano_vencido';
            return (
              <tr key={t.id} className={`transition-colors hover:bg-ink/[0.03] ${selecionado ? 'bg-brand/5' : ''}`}>
                <td className="px-3 py-2.5 align-top">
                  <input
                    type="checkbox"
                    checked={selecionado}
                    disabled={!selecionado && !podeSelecionarMais}
                    onChange={() => onComparar(t)}
                    aria-label={`Comparar ${t.name}`}
                    title={!selecionado && !podeSelecionarMais ? 'Já há dois modelos escolhidos: desmarque um para trocar' : 'Escolha dois modelos para comparar lado a lado'}
                    className="mt-1 h-4 w-4 accent-[rgb(var(--color-brand))]"
                  />
                </td>
                <td className="max-w-[20rem] px-3 py-2.5 align-top">
                  <div className="flex items-start gap-2">
                    <Icon name={CANAL_ICONE[t.channel]} size="sm" className="mt-0.5 shrink-0 text-brand" />
                    <div className="min-w-0">
                      <button type="button" onClick={() => onAbrir(t)} className="block max-w-full truncate text-left font-mono text-sm font-semibold text-ink hover:text-brand hover:underline" title={t.name}>
                        {t.name}
                      </button>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5">
                        <Badge tone={STATUS_TOM[t.status]} dot>
                          {statusLabel(t.status, t.channel)}
                        </Badge>
                        {pos?.melhor === true ? (
                          <Badge tone="good" icon="estrela">
                            Melhor
                          </Badge>
                        ) : null}
                        {empresa === null ? (
                          <Badge tone="brand">Global</Badge>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-ink-muted">
                            <CompanyLogo company={empresa} size="sm" />
                            <span className="truncate">{empresa.name}</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-2.5 align-top text-xs text-ink-muted">{descreverPublico(t)}</td>
                <td className="px-3 py-2.5 text-right align-top tabular-nums text-ink">{p.sent === 0 ? <span className="text-ink-subtle">—</span> : formatCount(p.sent)}</td>
                <td className="px-3 py-2.5 text-right align-top tabular-nums text-ink">
                  {p.sent === 0 ? (
                    <span className="text-ink-subtle">—</span>
                  ) : (
                    <>
                      {formatCount(p.replied)} <span className="text-xs text-ink-subtle">{formatRatio(p.replied, p.delivered)}</span>
                    </>
                  )}
                </td>
                <td className="px-3 py-2.5 text-right align-top tabular-nums text-ink">{p.clicked === null || p.sent === 0 ? <span className="text-ink-subtle">—</span> : formatRatio(p.clicked, p.delivered)}</td>
                <td className="px-3 py-2.5 text-right align-top tabular-nums text-ink">
                  {!credita ? (
                    <span className="text-ink-subtle" title="Este público não gera crédito próprio">
                      —
                    </span>
                  ) : p.sent === 0 ? (
                    <span className="text-ink-subtle">—</span>
                  ) : (
                    <div className="flex flex-col items-end">
                      <span>{formatCount(p.paid)}</span>
                      <Variacao atual={p.paid} anterior={a === null ? null : a.paid} compacto />
                    </div>
                  )}
                </td>
                <td className="px-3 py-2.5 text-right align-top tabular-nums text-ink">{credita && p.sent > 0 ? formatRatio(p.paid, p.sent) : <span className="text-ink-subtle">—</span>}</td>
                <td className="px-3 py-2.5 text-right align-top tabular-nums text-ink">{credita && p.sent > 0 ? formatBRLFromCents(p.recoveredCents) : <span className="text-ink-subtle">—</span>}</td>
                <td className="px-3 py-2.5 text-right align-top text-xs text-ink-subtle">{formatDateTime(t.updatedAt)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
